//#region IMPORTS
import {
  setInstoreOrderParams,
  clearInstoreOrderParam,
  clearInstoreOrderParams,
} from "../../features/instoreOrders/instoreOrdersSlice";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { InstoreOrder } from "../../models/instoreOrder";
import { ReactNode } from "react";
import MainFilterBarItem from "../_layout/MainFilterBarItem";
import getDistinctValues from "../../helpers/getDistinctValues";
import usePickerManager from "../../hooks/usePickerManager";
import useAdminManager from "../../hooks/useAdminManager";
import MainFilterBar from "../_layout/MainFilterBar";
import useHQmanager from "../../hooks/useHQmanager";
//#endregion

//#region INTERFACE
interface Props {
  extensionButtons?: ReactNode;
  hideStatusFilter?: boolean;
  hideTypeFilter?: boolean;
  instoreOrders: InstoreOrder[];
}
//#endregion

export default function InstoreOrdersFilterBar({
  extensionButtons,
  hideStatusFilter = false,
  hideTypeFilter = false,
  instoreOrders,
}: Props) {
  //#region SETUP
  // Redux
  const dispatch = useAppDispatch();
  const { page, limit, sort, ...params } = useAppSelector(
    ({instoreOrders}) => instoreOrders.instoreOrder_params
  );
  const { loading } = useAppSelector(({instoreOrders}) => instoreOrders);
  const { user } = useAppSelector(({account}) => account);

  const isPicker = usePickerManager(user);
  const isAdmin = useAdminManager(user);
  const isHQ = useHQmanager(user);

  // Preset-locked filters can't be cleared from the badge list
  const paramsDisabledList = ["order_status", "order_type_instore_name"].filter(
    (param) =>
      (param === "order_status" && hideStatusFilter) ||
      (param === "order_type_instore_name" && hideTypeFilter)
  );
  //#endregion

  //#region RENDER
  return (
    <MainFilterBar
      paramNamesList={params}
      paramsDisabledList={paramsDisabledList}
      clearAllFiltersHandler={() => dispatch(clearInstoreOrderParams())}
      extensionButtons={extensionButtons}
      clearFilterHandler={(paramName: string) => {
        dispatch(clearInstoreOrderParam(paramName));
      }}
      disabled={loading}
    >
      {/* order_code */}
      <MainFilterBarItem
        filterParameterName="order_code"
        displayExplanation="Search the instore orders by order code"
        clearFilterAction={() => dispatch(clearInstoreOrderParam("order_code"))}
        selectedValue={params.order_code}
        filterAction={(value: string) =>
          dispatch(setInstoreOrderParams({ order_code: value }))
        }
        displayText="Order code"
        selected={!!params.order_code}
      />
      
      {/* external_store_id */}
      <MainFilterBarItem
        filterParameterName="store_number"
        displayExplanation="Search the instore orders by external store number"
        clearFilterAction={() =>
          dispatch(clearInstoreOrderParam("external_store_id"))
        }
        selectedValue={params.external_store_id}
        filterAction={(value: string) =>
          dispatch(setInstoreOrderParams({ external_store_id: value }))
        }
        displayText="Store ID"
        selected={!!params.external_store_id}
        hide={!isAdmin && !isHQ && !isPicker}
      />

      {/* language */}
      <MainFilterBarItem
        filterParameterName="language"
        displayExplanation="Filter the orders by language"
        clearFilterAction={() => dispatch(clearInstoreOrderParam("language"))}
        selectBoxItems={getDistinctValues(
          instoreOrders,
          "store_language"
        ).filter((item) => item !== null)}
        selectedValue={params.language}
        filterAction={(value: string) => {
          dispatch(setInstoreOrderParams({ language: value }));
        }}
        displayText="Language"
        selectBox
        selected={!!params.language}
        hide={!isAdmin && !isHQ && !isPicker}
      />

      {/* order_status */}
      <MainFilterBarItem
        filterParameterName="order_status"
        displayExplanation="Filter the orders by order status"
        clearFilterAction={() =>
          dispatch(clearInstoreOrderParam("order_status"))
        }
        selectBoxItems={getDistinctValues(
          instoreOrders,
          "order_status_name"
        ).filter((item) => item !== null)}
        selectedValue={params.order_status}
        filterAction={(value: string) => {
          dispatch(setInstoreOrderParams({ order_status: value }));
        }}
        displayText="Order status"
        selectBox
        selected={!!params.order_status}
        hide={hideStatusFilter}
      />

      {/* order_type_instore_name */}
      <MainFilterBarItem
        filterParameterName="order_type_instore_name"
        displayExplanation="Filter the orders by order type"
        clearFilterAction={() => dispatch(clearInstoreOrderParam("order_type_instore_name"))}
        selectBoxItems={getDistinctValues(
          instoreOrders,
          "order_type_instore_name"
        ).filter((item) => item !== null)}
        selectedValue={params.order_type_instore_name}
        filterAction={(value: string) => {
          dispatch(setInstoreOrderParams({ order_type_instore_name: value }));
        }}
        displayText="Order type"
        selectBox
        selected={!!params.order_type_instore_name}
        hide={hideTypeFilter}
      />
    </MainFilterBar>
  );
  //#endregion
}
